import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { Form, Table } from "react-bootstrap";
import { fetchAllDataAPI, filterByParams } from "../../utils/api";
import Skeleton from "react-loading-skeleton";
import { numberFormat } from "../../utils/numberFormat";
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import moment from "moment";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";

const categoryOrder = ["Courier", "Dealer", "Post"];

const CompanyWiseReport = () => {
    const [report, setReport] = useState([]);
    const [dateBlocks, setDateBlocks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [reportStartDate, setReportStartDate] = useState(null);
    const [reportEndDate, setReportEndDate] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: new Date().toLocaleDateString('en-US'),
        endDate: new Date().toLocaleDateString('en-US'),
    });

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI("super-admin-get-report-company-wise-of-current-month");
            if (res.data.status === 1) {
                const result = res.data.report;
                setReport(result);
                const allBlocks = new Set();
                result.forEach(company =>
                    company.blocks.forEach(block => allBlocks.add(block.block_label))
                );
                setDateBlocks([...allBlocks]);
                setReportStartDate(res.data.report_start_date);
                setReportEndDate(res.data.report_end_date);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
        }
        setIsLoading(false);
    };

    const handleDateRangeFilter = async (event, picker) => {
        setIsLoading(true);
        const range = {
            startDate: picker.startDate.format("YYYY-MM-DD"),
            endDate: picker.endDate.format("YYYY-MM-DD"),
        };
        setDateRange(range)
        try {
            const res = await filterByParams(
                "super-admin-get-filtered-company-wise-report-by-date-range",
                range
            );

            if (res.data.status === 1) {
                setReport(res.data.report);
                setDateBlocks(res.data.report[0]?.blocks.map(b => b.block_label) || []);
                setReportStartDate(res.data.report_start_date);
                setReportEndDate(res.data.report_end_date);
            }
        } catch (err) {
            console.error("Error loading filtered report", err);
        }

        setIsLoading(false);
    };

    const handleReset = () => window.location.reload();

    const filteredReport = report.filter(company =>
        company.company_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderCompanyTable = (company, index) => {
        const blockData = {};
        dateBlocks.forEach(label => {
            const found = company.blocks.find(b => b.block_label === label);
            blockData[label] = found || {};
        });

        return (
            <div key={index} className="mb-3">
                <h6 className="text-primary fw-bold mb-2">{company.company_name}</h6>
                <Table bordered responsive className="mb-4 small table-sm text-center">
                    <thead>
                        <tr>
                            <th style={{ width: "120px" }}>Details</th>
                            {dateBlocks.map((block, idx) => (
                                <th key={idx}>{block}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {categoryOrder.map(category => (
                            <tr key={category}>
                                <td className="text-start fw-bold">{category}</td>
                                {dateBlocks.map((block, idx) => (
                                    <td key={idx}>
                                        {blockData[block]?.[category]
                                            ? numberFormat(blockData[block][category])
                                            : "-"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        <tr className="fw-bold bg-light">
                            <td className="text-start fw-bold">Total</td>
                            {dateBlocks.map((block, idx) => {
                                const sum = categoryOrder.reduce((acc, cat) =>
                                    acc + (blockData[block]?.[cat] || 0), 0);
                                return <td key={idx} className="fw-bold">{numberFormat(sum)}</td>;
                            })}
                        </tr>
                    </tbody>
                </Table>
            </div>
        );
    };

    const renderSkeletonTable = () => {
        const placeholderBlocks = Array(6).fill("01–10 Jul");
        const rows = ["Courier", "Dealer", "Post", "Total"];

        return (
            <div className="mb-3">
                <h6 className="text-primary fw-bold mb-2"><Skeleton width={250} /></h6>
                <Table bordered responsive className="mb-4 small table-sm text-center">
                    <thead>
                        <tr>
                            <th style={{ width: "120px" }}>Details</th>
                            {placeholderBlocks.map((_, idx) => (
                                <th key={idx}><Skeleton width={70} /></th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((label, rowIdx) => (
                            <tr key={rowIdx}>
                                <td className="text-start"><Skeleton width={80} /></td>
                                {placeholderBlocks.map((_, colIdx) => (
                                    <td key={colIdx}><Skeleton width={90} /></td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR: REPORT</title>
            </Helmet>
            <ContentWrapper>
                <ReportNavBar />
                <div className="card p-2" style={{ marginBottom: "50px" }}>
                    <div className="heading d-flex justify-content-between mb-2">
                        <h5 className="mb-0 text-uppercase mt-2">
                            {isLoading || !reportStartDate || !reportEndDate ? (
                                <Skeleton width={280} height={20} />
                            ) : (
                                `Reports from ${moment(reportStartDate).format("MMMM D, YYYY")} TO ${moment(reportEndDate).format("MMMM D, YYYY")}`
                            )}
                        </h5>
                        <div className="mt-1">
                            <Form.Control
                                type="text"
                                placeholder="Search by Company Name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="mb-3"
                            />
                        </div>
                        <div>
                            <span onClick={handleReset} className="btnreset">Reset</span>
                            <DateRangePicker
                                initialSettings={{
                                    startDate: dateRange.startDate,
                                    endDate: dateRange.endDate,
                                }}
                                onApply={handleDateRangeFilter}
                            >
                                <button className="btn btn-sm btndateramge" style={{marginTop:"2px"}}>Select Date Range</button>
                            </DateRangePicker>
                        </div>
                    </div>

                    {/* Skeleton or Data */}
                    {isLoading
                        ? renderSkeletonTable()
                        : filteredReport.map((company, index) => renderCompanyTable(company, index))
                    }
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(CompanyWiseReport);
